import {
  ACCOMMODATION_PREFERENCE_OPTIONS,
  type AccommodationPreference,
} from '@/types/clientsApiTypes';

/** Matches API rule `preference_data.stay.accommodation_types.*` max length */
export const ACCOMMODATION_LEGACY_ENTRY_MAX_LENGTH = 255;

const KNOWN_KEYS = new Set<string>(ACCOMMODATION_PREFERENCE_OPTIONS.map((o) => o.key));

export function isKnownAccommodationPreferenceKey(value: string): boolean {
  return KNOWN_KEYS.has(value);
}

/**
 * Normalizes one element from `stay.accommodation_types` (string or `{ value, translated_value }` from CRM/API).
 */
function normalizeStayAccommodationEntry(raw: unknown): {
  bucket: 'known';
  key: AccommodationPreference;
} | {
  bucket: 'legacy';
  text: string;
} | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    if (KNOWN_KEYS.has(trimmed)) {
      return { bucket: 'known', key: trimmed as AccommodationPreference };
    }
    return { bucket: 'legacy', text: trimmed };
  }

  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    const item = raw as { value?: unknown; translated_value?: unknown };
    const val = typeof item.value === 'string' ? item.value.trim() : '';
    const tv = typeof item.translated_value === 'string' ? item.translated_value.trim() : '';

    if (val !== '' && KNOWN_KEYS.has(val)) {
      return { bucket: 'known', key: val as AccommodationPreference };
    }

    const legacyText = tv !== '' ? tv : val;
    if (legacyText === '') return null;
    return { bucket: 'legacy', text: legacyText };
  }

  return null;
}

/**
 * Splits `stay.accommodation_types` into standard keys vs legacy/free-text rows (CRM / imports).
 * Supports plain strings and `{ value, translated_value }` objects (same shapes as read-only profile UI).
 */
export function partitionAccommodationTypes(
  values: readonly unknown[] | null | undefined
): { legacy: string[]; known: AccommodationPreference[] } {
  const list = values ?? [];
  const legacy: string[] = [];
  const known: AccommodationPreference[] = [];

  for (const raw of list) {
    const normalized = normalizeStayAccommodationEntry(raw);
    if (!normalized) continue;
    if (normalized.bucket === 'known') {
      known.push(normalized.key);
    } else {
      legacy.push(normalized.text);
    }
  }

  return { legacy, known };
}

export function mergeAccommodationTypes(legacy: string[], known: AccommodationPreference[]): string[] {
  return [...legacy, ...known];
}

/**
 * CRM/API often stores standard keys in `stay.accommodation_types` and free-text rows in `stay.others`.
 * Returns one raw list for {@link flattenStayAccommodationTypesForForm} (types first, then others).
 */
export function combineStayAccommodationRawFromPreferenceStay(stay: unknown): unknown[] | null {
  if (stay == null || typeof stay !== 'object' || Array.isArray(stay)) {
    return null;
  }

  const s = stay as { accommodation_types?: unknown; others?: unknown };
  const types = Array.isArray(s.accommodation_types) ? s.accommodation_types : [];
  const others = Array.isArray(s.others) ? s.others : [];

  if (types.length === 0 && others.length === 0) {
    return null;
  }

  return [...types, ...others];
}

/**
 * Flat array of strings/keys for forms (drops `{ value, translated_value }` wrappers).
 */
export function flattenStayAccommodationTypesForForm(values: readonly unknown[] | null | undefined): string[] {
  const { legacy, known } = partitionAccommodationTypes(values);
  return mergeAccommodationTypes(legacy, known);
}
